'use client';

import { useState } from 'react';
import { useClerk } from '@clerk/nextjs';
import { toast } from 'react-toastify';

import { trpc } from '@/app/api/trpc/client';

const CONFIRM_PHRASE = 'DELETE';

const DeleteAccount = () => {
  const { signOut } = useClerk();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const { mutate: deleteAccount, isPending } =
    trpc.user.deleteAccount.useMutation({
      onSuccess: async () => {
        toast.success('Your account has been deleted.');
        await signOut();
      },
      onError: (error) => {
        toast.error('Failed to delete account: ' + error.message);
      },
    });

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    setConfirmText('');
  };

  const handleDelete = () => {
    if (confirmText !== CONFIRM_PHRASE) return;
    deleteAccount();
  };

  // Rendered inside Clerk's own <UserProfile /> modal (a portal). Headless
  // UI's <Dialog> also portals to document.body, which sits outside Clerk's
  // modal DOM subtree -- Clerk's own focus trap then sees focus "leave" its
  // modal the moment our input is focused and closes itself, taking this
  // dialog down with it. Rendering inline (no portal) avoids that entirely.
  if (!isConfirmOpen) {
    return (
      <section className='w-full space-y-3'>
        <button
          type='button'
          className='btn bg-red-600 text-white border-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700'
          onClick={() => setIsConfirmOpen(true)}
        >
          Delete account
        </button>
      </section>
    );
  }

  return (
    <section className='w-full max-w-md space-y-3 rounded-lg border border-red-300 dark:border-red-800 p-4'>
      <h2 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
        Delete account
      </h2>
      <p className='text-sm text-gray-700 dark:text-gray-200'>
        This will immediately sign you out and revoke access to your account.
      </p>
      <label
        htmlFor='delete-account-confirm'
        className='block text-sm font-medium text-gray-700 dark:text-gray-200'
      >
        Type {CONFIRM_PHRASE} to confirm
      </label>
      <input
        id='delete-account-confirm'
        type='text'
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        className='input input-bordered w-full px-3 py-2'
        autoComplete='off'
        autoCapitalize='off'
        spellCheck={false}
      />
      <div className='flex justify-end gap-2 pt-2'>
        <button
          type='button'
          onClick={closeConfirm}
          className='btn btn-secondary'
          disabled={isPending}
        >
          Cancel
        </button>
        <button
          type='button'
          onClick={handleDelete}
          disabled={confirmText !== CONFIRM_PHRASE || isPending}
          className='rounded-lg bg-red-600 px-8 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50'
        >
          {isPending ? 'Deleting…' : 'Delete account'}
        </button>
      </div>
    </section>
  );
};

export default DeleteAccount;
